import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  Stack,
  Button,
  Chip,
  alpha,
  Skeleton,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'src/context/auth/AuthContext';
import { useViewScope } from 'src/hooks/useViewScope';
import { studentService } from 'src/services/student.service';
import { studentBehaviourService } from 'src/services/student-behaviour.service';
import Page from 'src/components/Page';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { StudentBehavious } from './StudentBehavious';
import { BehaviourTypes } from './BehaviourTypes';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`streetcred-tabpanel-${index}`} aria-labelledby={`streetcred-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 1.5 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `streetcred-tab-${index}`,
    'aria-controls': `streetcred-tabpanel-${index}`,
  };
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  isLoading?: boolean;
}

function MetricCard({ title, value, subtitle, icon, iconBg, iconColor, isLoading }: MetricCardProps) {
  return (
    <Card
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={50} height={28} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', my: 0.25, fontSize: '1.25rem', letterSpacing: '-0.01em' }}>
            {value}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.75,
          bgcolor: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon icon={icon} width={20} height={20} />
      </Box>
    </Card>
  );
}

export default function ManageStreetCredsAdmin(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { getBaseRoute, hasPermission, can, user } = useAuth();
  const [currentTab, setCurrentTab] = React.useState<number>((location.state as { tab?: number })?.tab ?? 0);

  const { isSelfScoped: isStudent, ownerFilterId: teacherUserId } = useViewScope('street_cred');

  const { data: studentId } = useQuery({
    queryKey: ['studentIdByUserId', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: isStudent && !!user?.id,
  });

  const { data: metrics, isLoading: isMetricsLoading } = useQuery({
    queryKey: ['streetCredMetrics', { teacherUserId, studentId, isStudent }],
    queryFn: () =>
      studentBehaviourService.getStreetCredMetrics({
        teacherUserId: isStudent ? undefined : teacherUserId,
        studentId: isStudent ? (studentId as string) : undefined,
      }),
    enabled: !isStudent || !!studentId,
    staleTime: 30000,
  });

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: 'S.T.R.E.E.T Cred', path: getBaseRoute('/street-cred') },
      { label: isStudent ? 'My Behavior Log' : currentTab === 0 ? 'Scholar Behavior Log' : 'Behavior Types Library' },
    ],
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const totalBehaviors = metrics?.totalBehaviors ?? 0;
  const positiveCount = metrics?.positiveCount ?? 0;
  const negativeCount = metrics?.negativeCount ?? 0;
  const netPoints = metrics?.netPoints ?? 0;
  const positiveRatio = metrics?.positiveRatio ?? 100;
  const totalTypes = metrics?.totalTypes ?? 0;

  const canManageTypes = hasPermission('street_cred.manage');
  const canCreateBehavior = hasPermission('street_cred.manage');

  return (
    <Page title="S.T.R.E.E.T Cred | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: 'rgba(124, 58, 237, 0.12)',
                    color: '#7C3AED',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon icon="solar:star-rainbow-bold-duotone" width={22} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                    S.T.R.E.E.T Cred
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {isStudent
                      ? 'Track your character points, merit highlights, and school recognition.'
                      : 'Recognize character excellence, log scholar behavior points, and foster school values.'}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              {canManageTypes && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon icon="solar:library-bold" width={16} />}
                  onClick={() => navigate(getBaseRoute('/add-behavior-type'))}
                  sx={{
                    height: 36,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
                  }}
                >
                  Catalog Library
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* 5 Top KPI Cards */}
        {isStudent ? (
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Net Cred Score"
                value={`${netPoints >= 0 ? '+' : ''}${netPoints} pts`}
                subtitle="Overall behavior balance"
                icon="solar:star-bold-duotone"
                iconBg="rgba(245, 158, 11, 0.12)"
                iconColor="#D97706"
                isLoading={isMetricsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Merits"
                value={positiveCount}
                subtitle="Positive highlights awarded"
                icon="solar:medal-ribbon-star-bold-duotone"
                iconBg="rgba(16, 185, 129, 0.12)"
                iconColor="#059669"
                isLoading={isMetricsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Demerits"
                value={negativeCount}
                subtitle="Improvement opportunities"
                icon="solar:danger-triangle-bold-duotone"
                iconBg="rgba(239, 68, 68, 0.12)"
                iconColor="#DC2626"
                isLoading={isMetricsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Positive Ratio"
                value={`${positiveRatio}%`}
                subtitle="Merit consistency score"
                icon="solar:chart-2-bold-duotone"
                iconBg="rgba(59, 130, 246, 0.12)"
                iconColor="#2563EB"
                isLoading={isMetricsLoading}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Total Behaviors"
                value={totalBehaviors}
                subtitle="Logged incidents"
                icon="solar:card-2-bold-duotone"
                iconBg="rgba(37, 99, 235, 0.12)"
                iconColor="#2563EB"
                isLoading={isMetricsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Positive Highlights"
                value={positiveCount}
                subtitle="Recognition awarded"
                icon="solar:medal-ribbon-star-bold-duotone"
                iconBg="rgba(16, 185, 129, 0.12)"
                iconColor="#059669"
                isLoading={isMetricsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Challenges"
                value={negativeCount}
                subtitle="Intervention needed"
                icon="solar:shield-warning-bold-duotone"
                iconBg="rgba(239, 68, 68, 0.12)"
                iconColor="#DC2626"
                isLoading={isMetricsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Net Velocity"
                value={netPoints > 0 ? `+${netPoints}` : `${netPoints}`}
                subtitle="Net points delta"
                icon="solar:chart-2-bold-duotone"
                iconBg="rgba(124, 58, 237, 0.1)"
                iconColor="#7C3AED"
                isLoading={isMetricsLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Positive Ratio"
                value={`${positiveRatio}%`}
                subtitle="Schoolwide ratio"
                icon="solar:pie-chart-2-bold-duotone"
                iconBg="rgba(217, 119, 6, 0.12)"
                iconColor="#D97706"
                isLoading={isMetricsLoading}
              />
            </Grid>
          </Grid>
        )}

        {/* Sub-Tabs Navigation (Catalog Management vs Behavior Log) */}
        {canManageTypes ? (
          <>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                mb: 1.5,
                p: 0.5,
                bgcolor: 'background.paper',
              }}
            >
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                sx={{
                  minHeight: 36,
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                  '& .MuiTab-root': {
                    minHeight: 36,
                    py: 0.5,
                    px: 2,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      fontWeight: 700,
                    },
                  },
                }}
              >
                <Tab
                  icon={<Icon icon="solar:user-check-bold" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>Scholar Behavior Log</span>
                      <Chip
                        label={totalBehaviors}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: currentTab === 0 ? 'primary.main' : 'action.selected',
                          color: currentTab === 0 ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                  {...a11yProps(0)}
                />
                <Tab
                  icon={<Icon icon="solar:library-bold" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>Behavior Types Library</span>
                      <Chip
                        label={totalTypes}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: currentTab === 1 ? 'primary.main' : 'action.selected',
                          color: currentTab === 1 ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                  {...a11yProps(1)}
                />
              </Tabs>
            </Card>

            <TabPanel value={currentTab} index={0}>
              <StudentBehavious />
            </TabPanel>

            <TabPanel value={currentTab} index={1}>
              <BehaviourTypes />
            </TabPanel>
          </>
        ) : (
          <StudentBehavious />
        )}
      </Container>
    </Page>
  );
}

export { ManageStreetCredsAdmin as StreetCreds };
